import React, { type PropsWithChildren, useCallback, useMemo, useRef } from 'react';

import memoize from 'lodash/memoize';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { type OnOAuthSuccessCallback } from '@atlassian/rovo-connectors-config/hooks/useRovoConfig';
import { getExperimentConfig } from '@atlassian/search-experiment/statsig';

import type { ApplicationModes } from '../../../../../common/constants/application-modes';
import {
	type BootstrapState,
	type ClearReferrerCallback,
	initialState,
	type OnSearchCompletedCallback,
	type RefreshSearchSessionIdCallback,
	type SetThirdPartyConfigsCallback,
} from '../../../../../common/constants/bootstrap';
import { ALL_FILTER_EXCLUDED_PRODUCTS } from '../../../../../common/constants/filters';
import type { ProductKeys } from '../../../../../common/constants/products';
import { getAllProductKeys } from '../../../../../common/constants/products';
import type { ThirdPartyConfigsBootstrap } from '../../../../../common/constants/schemas/3p-config';
import type { Overrides } from '../../../../../common/constants/schemas/query-params';
import type { SearchStoreExperience } from '../../../../../common/constants/search';
import { SearchExperience } from '../../../../../common/constants/search-experience';
import { getMultiSiteSearchInRovo } from '../../../../../common/utils/experiments';
import { getAccessibleProducts } from '../../../../../common/utils/get-accessible-products';
import { getApplicationMode } from '../../../../../common/utils/get-application-mode';
import { isHello } from '../../../../../common/utils/is-hello';
import { parseQueryParams } from '../../../../../common/utils/query-params';
import { logException } from '../../../../error-boundary/log-exception';
import { type BootstrapContainerProps, type QueryParamsCallback } from '../../index';
import { serveBootstrapProviderExp } from '../../utils';
import { StaticBootstrapContext, StaticBootstrapStoreContext } from '../index';
import { createStaticBootstrapStore, type StaticBootstrapStore } from '../static-bootstrap-store';

export type StaticContextProviderProps = {
	refreshSearchSessionId: RefreshSearchSessionIdCallback;
	clearReferrer: ClearReferrerCallback;
	initialText: string | undefined;
	initialThirdPartyConfigs: Partial<ThirdPartyConfigsBootstrap>;
	experience?: SearchStoreExperience;
	accessibleProducts?: ProductKeys[];
	onOAuthSuccess?: OnOAuthSuccessCallback;
};

export type StaticBootstrapContextState = Omit<
	BootstrapState,
	| 'queryParams'
	| 'referrer'
	| 'queryParamsCallback'
	| 'searchSessionId'
	| 'smartLinkUsage'
	| 'smartLinkUsageAndTopApps'
	| 'prevQueryParams'
	| 'initialSpaceARIs'
> & {
	// Added the callback wasnt being stored in state (good) and now is in static context, and is not undefined, because we always set noop in state initialiser
	queryParamsCallback: QueryParamsCallback;
	refreshSearchSessionId: RefreshSearchSessionIdCallback;
	clearReferrer: ClearReferrerCallback;
	initialTextQueryParam: string | undefined;
	setThirdPartyConfigs: SetThirdPartyConfigsCallback;
	onSearchCompleted: OnSearchCompletedCallback;
	experience: SearchStoreExperience;
	applicationMode: ApplicationModes;
	multiSiteSearchInRovo: boolean;
	isHello: boolean;
};

// Attempting to build defaultstate from the same underlying definitions to ease migration to minimise migration
// omit from initialState
const {
	queryParams: _queryParams,
	referrer: _referrer,
	searchSessionId: _searchSessionId,
	thirdPartyConfigs: _thirdPartyConfigs,
	prevQueryParams: _prevQueryParams,
	initialSpaceARIs: _initialSpaceARIs,
	...inheritedInitialState
} = initialState;
export const defaultStaticContext: {
	initialTextQueryParam: undefined;
	queryParamsCallback: () => void;
	refreshSearchSessionId: () => void;
	clearReferrer: () => void;
	setThirdPartyConfigs: SetThirdPartyConfigsCallback;
	onSearchCompleted: () => void;
	experience: SearchStoreExperience;
	applicationMode: 'rovo' | 'unified';
	multiSiteSearchInRovo: boolean;
	isHello: boolean;
	thirdPartyConfigs: Partial<ThirdPartyConfigsBootstrap>;
	products: never[];
	nouns: never[];
	allFilterExcludedProducts: never[];
	overrides?: Overrides;
	experimentConfig: {
		backendExperiment: undefined;
		frontendExperiments: undefined;
		dynamicExperiments: undefined;
		combinedExperiment: undefined;
	};
	experimentLayers: never[];
	isRovoEnabled: false;
	isConfigLoaded: false;
	rovoFeatures: {};
	isJiraIssueIndexed: false;
	productEdition: undefined;
	productDataRegions: undefined;
	linkedCloudARIs: undefined;
	analyticsAttributes: undefined;
	aggAbsoluteUrl: undefined;
	cloudId: undefined;
	aggregatorHostName: undefined;
	SAINHostName: undefined;
	user: undefined;
	primaryProduct: undefined;
	primarySubProduct: undefined;
	orgId: undefined;
	intercomHmac: undefined;
	features: undefined;
	hasEditOrViewPlanPermission: false;
	smartLinkUsage: undefined;
	smartLinkUsageAndTopApps: undefined;
	onOAuthSuccess: undefined;
} = {
	...inheritedInitialState,
	initialTextQueryParam: undefined,
	queryParamsCallback: (): void => {},
	refreshSearchSessionId: (): void => {},
	clearReferrer: (): void => {},
	setThirdPartyConfigs: (() => {}) as SetThirdPartyConfigsCallback,
	onSearchCompleted: (): void => {},
	experience: SearchExperience.SEARCH_PAGE as SearchStoreExperience,
	applicationMode: getApplicationMode({ isRovoEnabled: inheritedInitialState.isRovoEnabled }),
	multiSiteSearchInRovo: getMultiSiteSearchInRovo(inheritedInitialState.experimentConfig),
	isHello: isHello(inheritedInitialState.cloudId),
	thirdPartyConfigs: {} as Partial<ThirdPartyConfigsBootstrap>,
};

/**
 * Conceptually this is a large single useMemo around as many pieces of state as we can guarantee do not change after the bootstrap phase (isConfigLoaded)
 */
export const StaticContextProvider = ({
	children,
	initialProps: props,
	queryParams,
	queryParamsCallback,
	refreshSearchSessionId,
	initialText,
	clearReferrer,
	initialThirdPartyConfigs,
	onSearchCompleted,
	onOAuthSuccess,
	experience,
	accessibleProducts,
}: PropsWithChildren<BootstrapContainerProps & StaticContextProviderProps>): React.JSX.Element => {
	/**
	 * Static lock version counter. The useMemo recomputes whenever this value changes.
	 *
	 * Version semantics:
	 * - 0: Lock open (bootstrap config not yet loaded)
	 * - 1: Lock closed (bootstrap config loaded, values frozen)
	 * - >1: Lock was reopened post-bootstrap via setThirdPartyConfigs (the ONLY path that increments above 1)
	 *
	 * If isConfigLoaded is already true on first render, starts at 1 so values are immediately available
	 * without an extra render cycle.
	 *
	 * Post-bootstrap, setThirdPartyConfigs can increment the counter above 1 to force the useMemo to
	 * recompute with updated refs. This is an expensive action that should be used sparingly and only
	 * in response to uncommon user actions (e.g. OAuth connect flow).
	 *
	 * IMPORTANT: effectiveLockVersion is the SOLE dependency of the static useMemo. Do not add other dependencies.
	 * If you need the useMemo to recompute for a new value, store that value in a ref and reopen the lock by incrementing this counter.
	 */
	const [staticLockVersion, setStaticLockVersion] = React.useState(() =>
		shouldStaticLockClose({ isConfigLoaded: props.isConfigLoaded || false }) ? 1 : 0,
	);
	/**
	 * Close the static lock synchronously during render when isConfigLoaded becomes true.
	 * Sets the version to exactly 1 (not an increment) so that only setThirdPartyConfigs can push the version above 1.
	 */
	if (
		staticLockVersion === 0 &&
		shouldStaticLockClose({ isConfigLoaded: props.isConfigLoaded || false })
	) {
		setStaticLockVersion(1);
	}

	// Create a stable store instance once per component lifetime
	const storeRef = useRef<StaticBootstrapStore | null>(null);

	/**
	 * Derive the effective lock version synchronously from both the state counter AND props.isConfigLoaded.
	 *
	 * When isConfigLoaded transitions from false to true, the in-render setState above schedules a
	 * state update, but staticLockVersion still reads as 0 on THIS render. Previously, the useMemo
	 * (which depended solely on staticLockVersion) would not recompute until the NEXT render when
	 * staticLockVersion became 1 — costing an extra render cycle (~100ms+ in a complex tree).
	 *
	 * effectiveLockVersion resolves this by treating the lock as closed immediately when
	 * isConfigLoaded is true, even before the state update takes effect. The useMemo now depends
	 * on effectiveLockVersion, so it recomputes on the same render that isConfigLoaded becomes true.
	 *
	 * For setThirdPartyConfigs (version > 1), the state-based counter still drives recomputation
	 * as before, since isConfigLoaded is already true at that point.
	 */
	const effectiveLockVersion =
		staticLockVersion === 0 &&
		shouldStaticLockClose({ isConfigLoaded: props.isConfigLoaded || false })
			? 1
			: staticLockVersion;

	/**
	 * thirdPartyConfigs is expected to be static after bootstrap, but can be overridden during OAuth workflow as a cheap "cache bust".
	 *
	 * Stored in a ref so that updates do not directly cause re-renders. Instead, the OAuth callback
	 * (setThirdPartyConfigs) updates the ref and reopens the static lock for exactly one render,
	 * allowing the useMemo to recompute with the new value.
	 */
	const thirdPartyConfigsRef = useRef<Partial<ThirdPartyConfigsBootstrap>>(
		initialThirdPartyConfigs || {},
	);
	// Sync thirdPartyConfigs from props when config finishes loading (before static lock closes).
	// Uses staticLockVersion (not staticLockClosed/effectiveLockVersion) because when isConfigLoaded
	// transitions to true, effectiveLockVersion eagerly resolves to 1 on the same render. The ref
	// must be updated BEFORE the useMemo (which depends on effectiveLockVersion) recomputes, so we
	// check the underlying state which is still 0 on the transition render.
	if (staticLockVersion === 0 && props.isConfigLoaded) {
		thirdPartyConfigsRef.current = initialThirdPartyConfigs || {};
	}
	const setThirdPartyConfigs: SetThirdPartyConfigsCallback = useCallback(
		(configs: Partial<ThirdPartyConfigsBootstrap>) => {
			thirdPartyConfigsRef.current = configs;
			// Reopen the static lock for exactly one render so the useMemo recomputes with the new thirdPartyConfigs
			setStaticLockVersion((v) => v + 1);
		},
		[],
	);
	const useMemoStaticProps: StaticBootstrapContextState = useMemo(() => {
		const enableRovoSearchOverrides = Boolean(
			// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
			FeatureGates.getExperimentValue('enable-rovo_search_overrides', 'value', false),
		);
		const parsedQueryParams =
			queryParams && enableRovoSearchOverrides ? parseQueryParams(queryParams) : undefined;

		const isRovoEnabled = props.isRovoEnabled || defaultStaticContext.isRovoEnabled;
		const cloudId = props.cloudId || defaultStaticContext.cloudId;
		const primaryProduct = props.primaryProduct || defaultStaticContext.primaryProduct;
		const primarySubProduct = props.primarySubProduct || defaultStaticContext.primarySubProduct;
		const user = props.user || defaultStaticContext.user;

		// Under rovo-search-remove-rss-bootstrap-fg, compute experimentConfig directly
		// via getExperimentConfig instead of receiving it through props from BootstrapStoreProvider.
		// This moves the experiment config computation into the static context where it belongs,
		// using props already available here. If a prop is explicitly provided, use it as an
		// override (supports backward compat during rollout and test scenarios).

		// Keep in sync with: bootstrap-store-provider/index.tsx
		const experimentConfig = serveBootstrapProviderExp()
			? props.experimentConfig ||
				getExperimentConfig({
					primaryProduct,
					experimentLayers: props.experimentLayers ?? [],
					sideBySideExperiment: props.features?.sideBySide?.experiments,
					statsigLayer: undefined,
					onError: (error: Error) => logException(error, user?.id, primaryProduct),
				})
			: props.experimentConfig || defaultStaticContext.experimentConfig;
		// This can contain duplicates from e.g. a full drive connector and a drive smartlink connector
		// Under rovo-search-remove-rss-bootstrap-fg, getAccessibleProducts is computed here
		// instead of in bootstrap-store-provider. On the old path, products is already processed.
		const products = serveBootstrapProviderExp()
			? dedupProducts(
					getAccessibleProducts({
						supportedProducts: getAllProductKeys(),
						products: props.products,
						accessibleProducts,
					}),
					defaultStaticContext.products,
				)
			: dedupProducts(props.products || [], defaultStaticContext.products);
		return {
			// Literal constant
			allFilterExcludedProducts: ALL_FILTER_EXCLUDED_PRODUCTS,

			// Static from internal props to BootstrapProvider
			experience: experience || defaultStaticContext.experience,

			// Static from host product props
			cloudId: cloudId,
			isHello: isHello(cloudId),
			aggregatorHostName: props.aggregatorHostName || defaultStaticContext.aggregatorHostName,
			aggAbsoluteUrl: props.aggAbsoluteUrl || defaultStaticContext.aggAbsoluteUrl,
			SAINHostName: props.SAINHostName || defaultStaticContext.SAINHostName,
			orgId: props.orgId || defaultStaticContext.orgId,
			primaryProduct,
			primarySubProduct,
			analyticsAttributes: props.analyticsAttributes || defaultStaticContext.analyticsAttributes,
			features: props.features || defaultStaticContext.features,
			experimentLayers: props.experimentLayers || defaultStaticContext.experimentLayers,
			queryParamsCallback: queryParamsCallback || defaultStaticContext.queryParamsCallback,
			onSearchCompleted:
				onSearchCompleted || props.onSearchCompleted || defaultStaticContext.onSearchCompleted,
			onOAuthSuccess: onOAuthSuccess || props.onOAuthSuccess || defaultStaticContext.onOAuthSuccess,
			user,

			// Extracted on first load from queryParams (host product props)
			initialTextQueryParam: initialText,

			// DYNAMIC: from bootstrap config -> isConfigLoaded
			isConfigLoaded: props.isConfigLoaded || defaultStaticContext.isConfigLoaded,
			intercomHmac: props.intercomHmac || defaultStaticContext.intercomHmac,
			isRovoEnabled,
			applicationMode: getApplicationMode({ isRovoEnabled }),
			nouns: props.nouns || defaultStaticContext.nouns,
			productEdition: props.productEdition || defaultStaticContext.productEdition,
			productDataRegions: props.productDataRegions || defaultStaticContext.productDataRegions,
			rovoFeatures: props.rovoFeatures || defaultStaticContext.rovoFeatures,
			linkedCloudARIs: props.linkedCloudARIs || defaultStaticContext.linkedCloudARIs,
			isJiraIssueIndexed: props.isJiraIssueIndexed || defaultStaticContext.isJiraIssueIndexed,
			hasEditOrViewPlanPermission:
				props.hasEditOrViewPlanPermission || defaultStaticContext.hasEditOrViewPlanPermission,
			// when removing this feature flag, update the type to no longer be optional
			...(enableRovoSearchOverrides && {
				overrides: parsedQueryParams?.overrides || initialState.overrides,
			}),

			// Third party configs - static after bootstrap, mutable only via OAuth callback (read from ref)
			thirdPartyConfigs: thirdPartyConfigsRef.current,

			// passed directly through from props
			refreshSearchSessionId,
			clearReferrer,
			setThirdPartyConfigs,

			// experimentConfig is now computed directly in this static context under
			// rovo-search-remove-rss-bootstrap-fg via getExperimentConfig, or falls back to props on the old path.
			experimentConfig,
			multiSiteSearchInRovo: getMultiSiteSearchInRovo(experimentConfig),

			products,
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [effectiveLockVersion]); // Do not expand this dependency. See effectiveLockVersion JSDoc. Reach out to #help-search-plex or #search-plex-perf-squad with any questions

	// Create or sync the external store.
	// On first render this creates the store; on subsequent renders it notifies
	// only subscribers whose selected slice actually changed.
	if (storeRef.current === null) {
		storeRef.current = createStaticBootstrapStore(useMemoStaticProps);
	} else {
		storeRef.current.setState(useMemoStaticProps);
	}

	return (
		<StaticBootstrapContext.Provider value={useMemoStaticProps}>
			<StaticBootstrapStoreContext.Provider value={storeRef.current}>
				{children}
			</StaticBootstrapStoreContext.Provider>
		</StaticBootstrapContext.Provider>
	);
};

type StaticLockProps = {
	isConfigLoaded: boolean;
};

/**
 * Lock static context values after config is loaded to ensure they do not change during the session, as they are expected to be static after the bootstrap phase.
 * This also allows us to remove any dependencies on props that would cause unnecessary re-renders of the static context provider and all of its children
 *
 * The only values that should be here are booleans set by ASYNC behaviour that we must wait for (e.g. the config network call(s))
 * To add a new async dependency, provide a boolean e.g. myAsyncValue that is false until the async behaviour is complete, and then add it to the shouldStaticLockClose function.
 *
 * Note: This is on the search query critical performance path, any additional values added here should be carefully considered.
 * Reach out to #help-search-plex or #search-plex-perf-squad with any questions
 */
const shouldStaticLockClose = ({ isConfigLoaded }: StaticLockProps) => {
	return isConfigLoaded; // Set by `useFetchConfig`, supported by promise-reuse and localStorage caching
};

/**
 * The set of `products` as passed in as a prop has duplicates due to smartlinks etc (drive can appear several times!)
 * We uniq() this list for use within search
 */
const dedupProducts = memoize(
	(a: ProductKeys[], b: ProductKeys[]) => {
		return [...new Set([...a, ...b])];
	},
	(a: ProductKeys[], b: ProductKeys[]) => a.join('') + b.join(''),
);
